import _get from 'lodash/get';
import {
  createContext,
  type FC,
  type ReactNode,
  useContext,
  useMemo,
} from 'react';
import { CmsComponents, Contentful } from 'src/lib/constants';

type CmsEntry = {
  sys?: { id?: string };
  fields?: { components?: unknown };
};

export type HeroElement = 'header' | 'section';

// content types whose `fields.components` are rendered back through the
// component switch, so heroes nested inside them count toward page order
const NESTING_CONTENT_TYPES = new Set<string>([
  CmsComponents.BACKGROUND_CONTAINER,
  CmsComponents.HIDE_FROM_AEO,
  CmsComponents.GRID_CONTAINER,
  CmsComponents.GRID_COLUMN,
]);

export const findFirstHeroId = (components: unknown): string | undefined => {
  if (!Array.isArray(components)) {
    return undefined;
  }
  for (const component of components as CmsEntry[]) {
    const contentType = _get(component, Contentful.CONTENT_TYPE_ID);
    if (contentType === CmsComponents.HERO) {
      return component?.sys?.id;
    }
    if (NESTING_CONTENT_TYPES.has(contentType)) {
      const nestedHeroId = findFirstHeroId(component?.fields?.components);
      if (nestedHeroId) {
        return nestedHeroId;
      }
    }
  }
  return undefined;
};

const FirstHeroContext = createContext<{ firstHeroId?: string } | null>(null);

// Only the outermost provider decides; nested component switches (background
// containers, grid columns, paywall, etc.) pass straight through so the first
// hero is resolved against the whole page.
export const FirstHeroProvider: FC<{
  components: unknown;
  children: ReactNode;
}> = ({ components, children }) => {
  const ancestor = useContext(FirstHeroContext);
  const value = useMemo(() => {
    return { firstHeroId: findFirstHeroId(components) };
  }, [components]);

  if (ancestor) {
    return <>{children}</>;
  }
  return (
    <FirstHeroContext.Provider value={value}>
      {children}
    </FirstHeroContext.Provider>
  );
};

// Heroes rendered outside a provider (tests, Storybook) keep rendering as
// <header>. If the same hero entry is reused on a page, every copy matches
// the first hero id and renders as <header>.
export const useHeroElement = (sys?: { id?: string }): HeroElement => {
  const context = useContext(FirstHeroContext);
  if (!context?.firstHeroId || !sys?.id) {
    return 'header';
  }
  return sys.id === context.firstHeroId ? 'header' : 'section';
};
